import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useUsersListQuery } from 'api';
import { GoBackButtonWithTitle, NewConfirmDialog, Spinner } from 'components';
import { EditTaskForm } from 'features/tasks';
import { theme } from 'styles';
import { UserStatus } from 'types';

import { useTaskDetailsPage } from './hooks';
import { ContentW, PageHeaderW, PageW } from './styled';

export function TaskEditPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isLoading: isLoadingUsersList, usersList } = useUsersListQuery({
    status: [UserStatus.Active, UserStatus.Registered],
  });

  const {
    task,
    isOpen,
    isLoading,
    canDelete,
    handleOpenModal,
    handleDeleteTask,
    handleCloseModal,
  } = useTaskDetailsPage();

  if (isLoading || isLoadingUsersList) {
    return <Spinner />;
  }

  return (
    <PageW>
      <PageHeaderW>
        <GoBackButtonWithTitle
          title={t('tasks.details.pageTitle')}
          onClick={() => navigate(-1)}
          containerSx={{
            marginTop: 0,
            marginBottom: '16px',
            maxWidth: '100%',
          }}
          titleSx={{ fontSize: '16px', color: theme.palette.info.main, fontWeight: 500 }}
          headerSx={{
            '& .MuiIconButton-root': {
              backgroundColor: theme.palette.info.main,
              width: 24,
              height: 24,
              left: 0,
              '&:hover': { backgroundColor: theme.palette.info.dark },
              '& svg': { color: theme.palette.white.main, height: '11px' },
            },
          }}
        />
      </PageHeaderW>
      <ContentW>
        {task && usersList.length > 0 && (
          <EditTaskForm
            task={task}
            usersList={usersList}
            canDelete={canDelete}
            handleOpenModal={handleOpenModal}
          />
        )}
      </ContentW>
      <NewConfirmDialog
        title={t('tasks.details.removeModal.title', { taskName: task?.title })}
        subTitle={t('tasks.details.removeModal.subtitle')}
        open={isOpen}
        onConfirm={handleDeleteTask}
        onClose={handleCloseModal}
        submitLabel={t('tasks.details.removeModal.successButton')}
        cancelLabel={t('tasks.details.removeModal.cancelButton')}
      />
    </PageW>
  );
}
